import React, { useEffect, useState } from "react";
import { Volume2, Lock, Download } from "lucide-react";

export function Wordmark({ size = 30 }) {
  return (
    <div className="wm inline-block" style={{ fontSize: size, color: "var(--sm-chalk)" }} data-testid="wordmark">
      SUPER{"\u00A0"}<span className="wm-ring" aria-hidden="true" />VER
    </div>
  );
}

// Sports Mantra brand lockup — cream-on-transparent, glows on navy per DESIGN_NOTES L11.
export function SMLogo({ height = 30, opacity = 1, className = "" }) {
  return (
    <span className={"sm-logo-chip " + className} data-testid="sports-mantra-logo">
      <img src="/sports-mantra-logo-cream.png" alt="Sports Mantra" style={{ height, opacity }} />
    </span>
  );
}

export function OverDots({ total = 6, idx = 0, grid = [] }) {
  return (
    <div className="flex items-center justify-center gap-2" data-testid="over-progress-dots">
      {Array.from({ length: total }).map((_, i) => {
        const done = i < idx;
        const cur = i === idx;
        const wk = grid[i] === "W";
        return <span key={i} className={"dot" + (wk ? " w" : done ? " done" : cur ? " cur" : "")} />;
      })}
    </div>
  );
}

export function ScoreBar({ score, runsNeeded, ballsLeft, wickets }) {
  const cells = [
    ["Your Score", score],
    ["Runs Needed", runsNeeded],
    ["Balls Left", ballsLeft],
    ["Wickets", `${wickets}/2`],
  ];
  return (
    <div className="grid grid-cols-4 gap-2 p-3 panel-2" data-testid="decision-scorebar">
      {cells.map(([l, v], i) => (
        <div key={i} className="text-center">
          <div className="stat-label">{l}</div>
          <div className="stat-value tnum" style={{ color: i === 1 ? "var(--sm-blaze)" : "var(--sm-chalk)" }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

export function DeckBadge({ deck, freeHit }) {
  if (!deck) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="chip deck" data-testid="decision-deck-badge">{deck}</span>
      {freeHit && <span className="chip free-hit" data-testid="decision-free-hit-chip">FREE HIT · NO FEAR</span>}
    </div>
  );
}

const WORD = { "6": "SIX!", "4": "FOUR!", "2": "TWO!", "1": "SINGLE", "0": "DOT", "W": "OUT" };
const TIER = { "6": "gold", "4": "gold", "2": "warm", "1": "warm", "0": "dim", "W": "cold" };

export function OutcomeBlaze({ outcome, runs, isWicket, freeHitNote, timedOut }) {
  const word = WORD[outcome] || "DOT";
  const tone = TIER[outcome] || "dim";
  let sub;
  if (freeHitNote) sub = freeHitNote;
  else if (isWicket) sub = "WICKET · THE GROUND GOES COLD";
  else if (runs > 0) sub = `+${runs} ${runs === 1 ? "RUN" : "RUNS"}`;
  else sub = timedOut ? "THE RUN-UP EMPTIED" : "NO RUN";
  return (
    <div className="text-center py-2" data-testid="reveal-outcome-blaze">
      <div className={"outcome-word anim-blaze " + tone}>{word}</div>
      <div className="font-mono text-xs up tracking-[0.14em] mt-2" style={{ color: "rgba(244,239,230,0.75)" }}>{sub}</div>
    </div>
  );
}

export function MaidaanVoiceBlock({ line, onHear, speaking, hearing, hearLabel = "Hear the Maidaan", testid = "maidaan-voice-block" }) {
  return (
    <div className="maidaan" data-testid={testid}>
      <div className="maidaan-horizon" />
      <div className="maidaan-body">
        <span className={"wave" + (speaking ? " speaking" : "")} aria-hidden>
          <i /><i /><i /><i /><i />
        </span>
        <div className="maidaan-line anim-rise" style={{ marginTop: 10 }}>{line}</div>
        <div className="maidaan-sig">— The Maidaan</div>
        {onHear && (
          <button className="tap-hear" onClick={onHear} data-testid="maidaan-tap-to-hear">
            <Volume2 size={15} />
            {hearing ? "Listening…" : hearLabel}
          </button>
        )}
      </div>
    </div>
  );
}

const SYMBOL = { "6": "6", "4": "4", "2": "2", "1": "1", "0": "•", "W": "W" };
const GTIER = { "6": "gold", "4": "gold", "2": "warm", "1": "warm", "0": "dim", "W": "cold" };
export function BallGrid({ grid = [], testid = "ball-grid" }) {
  return (
    <div className="flex items-center justify-center gap-2 flex-wrap" data-testid={testid}>
      {grid.map((o, i) => (
        <div key={i} className={"tile " + (GTIER[o] || "dim")}>{SYMBOL[o] || "•"}</div>
      ))}
    </div>
  );
}

export function LockBadge() {
  return <span className="opt-lock-badge"><Lock size={9} style={{ display: "inline", marginRight: 3, verticalAlign: "-1px" }} />LOCKED</span>;
}

// Small breathing ring for loading states. Moved out of the retired FloodlitField.
export function BreathingRing({ label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4" data-testid="edge-loading">
      <div className="breath-ring" />
      {label && <div className="font-mono text-xs up tracking-[0.18em]" style={{ color: "rgba(244,239,230,0.7)" }}>{label}</div>}
    </div>
  );
}

// PWA add-to-home-screen affordance \u2014 the internalised trigger (\u00a71C) without push.
export function InstallChip({ label }) {
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  if (!prompt) return null;
  return (
    <button className="chip streak" onClick={async () => { try { prompt.prompt(); await prompt.userChoice; } catch (e) {} setPrompt(null); }} data-testid="install-pwa">
      <Download size={13} /> {label}
    </button>
  );
}
